import { css, Rule } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'
import { injectIntl, WrappedComponentProps } from 'react-intl'

import { notEmpty } from '../../utils'
import { clickDisplay, IInjectedClickDisplayProps } from '../../utils/click-display'

import { StyledText } from '../styled-text'
import { Flex } from '../flex'
import { ChevronIcon as ChevronIconBase } from '../chevron-icon'
import { Touchable } from '../touchable'

import Check from './check.svg'

import getStyles from './styles'
import { useTheme, withTheme, IWithThemeProps } from '../../theme'
import { UiVariant } from '../../types'

const cloneOption = (
  option: React.ReactElement,
  native: boolean,
  onChange: (event: React.ChangeEvent<HTMLSelectElement>, val: any) => void,
  selectedValue: any
) => {
  const { children, disabled, value } = option.props

  const key = value
  return !native
    ? React.cloneElement(option, {
        selected: Array.isArray(selectedValue) ? selectedValue.includes(value) : selectedValue === value,
        onClick: (event: React.ChangeEvent<HTMLSelectElement>) => {
          onChange(event, value)

          if (option.props.onClick) option.props.onClick(event)
        },
        key,
      })
    : React.createElement(
        'option',
        {
          disabled,
          value,
          key,
        },
        children
      )
}

const getLabelFromOption = (
  option: React.ReactElement,
  formatMessage: WrappedComponentProps['intl']['formatMessage']
) => {
  if (typeof option.props.label === 'object' && option.props.label.id) return formatMessage(option.props.label)
  else if (option.props.label) return option.props.label

  return option.props.children
}

const getSelectLabel = (
  options: React.ReactElement[],
  value: any,
  defaultValue: any,
  formatMessage: WrappedComponentProps['intl']['formatMessage']
) => {
  // Select the defaultValue if we haven't selected anything already
  if (!value && defaultValue) {
    const defaultOption = options.find(option => option.props.value === defaultValue) || null

    if (defaultOption) {
      return getLabelFromOption(defaultOption, formatMessage)
    }
  }

  const selected = options.filter(option =>
    Array.isArray(value) ? value.includes(option.props.value) : value === option.props.value
  )

  if (selected.length === 1) {
    return getLabelFromOption(selected[0], formatMessage)
  }

  if (selected.length > 1) {
    return selected.map(option => getLabelFromOption(option, formatMessage)).join(', ')
  }

  return defaultValue
}

export interface ISelectProps {
  innerRef?: React.RefObject<{}>
  readOnly?: boolean
  children?: React.ReactNode
  disabled?: boolean
  style?: Rule
  wrapperStyle?: Rule
  defaultValue?: string
  label?: React.ReactNode
  autoFocus?: boolean
  required?: boolean
  native?: boolean
  flush?: boolean
  onClick?: (event: React.SyntheticEvent, visible: boolean) => void
  onShow?: () => void
  onHide?: () => void
}

export interface INonNativeSelectProps<TValue> extends ISelectProps {
  id?: string
  value?: TValue
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>, val: TValue) => void
}

export interface INativeSelectProps extends ISelectProps {
  id?: string
  value?: string
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>, val: string) => void
}

/* eslint-disable jsx-a11y/no-onchange */

class WrappedSelect<TValue> extends React.Component<
  INonNativeSelectProps<TValue> &
    INativeSelectProps &
    WrappedComponentProps &
    IInjectedClickDisplayProps &
    IWithThemeProps
> {
  static propTypes = {
    value: PropTypes.any.isRequired,
    onChange: PropTypes.func.isRequired,
    setVisible: PropTypes.func,
    innerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    onClick: PropTypes.func,
    visible: PropTypes.bool,
    readOnly: PropTypes.bool,
    intl: PropTypes.object,
    children: PropTypes.node,
    native: PropTypes.bool,
    disabled: PropTypes.bool,
    style: PropTypes.object,
    wrapperStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    defaultValue: PropTypes.string,
    label: PropTypes.node,
    autoFocus: PropTypes.bool,
  }

  static displayName = 'Select'

  static defaultProps = {
    native: true,
  }

  state = {
    focused: false,
  }

  handleChange = (event: React.ChangeEvent<HTMLSelectElement>, val: string) => {
    const { onChange, setVisible, children } = this.props
    event.stopPropagation()
    setVisible(false)
    if (onChange) {
      onChange(event, this.stringifiedOptionToValue(children)[val])
    }
  }

  stringifiedOptionToValue = (children: React.ReactNode): { [key: string]: TValue } => {
    const elements = React.Children.toArray(children)
      .filter(child => React.isValidElement(child))
      .filter(notEmpty) as React.ReactElement[]

    return elements.reduce((acc: { [key: string]: TValue }, option) => {
      acc[`${option.props.value}`] = option.props.value
      return acc
    }, {})
  }

  render() {
    const {
      native = true,
      value,
      disabled,
      innerRef,
      onClick,
      visible,
      defaultValue,
      style,
      wrapperStyle,
      readOnly,
      intl,
      label,
      flush,
      autoFocus,
    } = this.props

    const options = React.Children.toArray(this.props.children)
      .map(child => (React.isValidElement(child) ? cloneOption(child, native, this.handleChange, value) : child))
      .filter(child => React.isValidElement(child)) as React.ReactElement[]

    const styles = getStyles(this.props.theme)

    const labelElements = [
      this.props.label && (
        <StyledText
          component="div"
          key="label"
          variant="dim"
          weight="600"
          {...css([
            styles.selectLabel.base,
            (disabled || readOnly) && styles.selectLabel.disabled,
            { overflow: 'visible', marginRight: this.props.theme.settings.spacing.xSmall },
          ])}
        >
          {label}
        </StyledText>
      ),
      <span
        key="value"
        {...css([
          styles.selectLabel.base,
          (disabled || readOnly) && styles.selectLabel.disabled,
          label && styles.selectLabel.withLabel,
        ])}
      >
        {getSelectLabel(options, value, defaultValue, intl.formatMessage)}
      </span>,
    ].filter(Boolean)

    return readOnly ? (
      <span {...css([styles.wrapper.base, flush && styles.wrapper.flush, wrapperStyle])} ref={innerRef}>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          row
          {...css([styles.select.base, !native && visible && styles.select.active, styles.select.disabled, style])}
        >
          {labelElements}
          <ChevronIcon active={false} />
        </Flex>
      </span>
    ) : (
      <span {...css([styles.wrapper.base, flush && styles.wrapper.flush, wrapperStyle])} ref={innerRef}>
        <Touchable
          onClick={(event: React.SyntheticEvent) =>
            !native && !disabled && onClick ? onClick(event, !visible) : undefined
          }
          {...css([
            styles.select.base,
            !disabled && styles.select.normal,
            !native && visible && styles.select.active,
            disabled && styles.select.disabled,
            this.state.focused && styles.select.focused,
            style,
          ])}
          tabIndex={native ? -1 : 0}
        >
          <Flex alignItems="center" flexGrow={1} justifyContent="space-between" row style={{ width: '100%' }}>
            {labelElements}
          </Flex>

          <ChevronIcon active={visible} color={label ? 'primary' : undefined} />
        </Touchable>
        {!native && visible && (
          <ul {...css(styles.selectList.base)}>
            {options.filter((option: React.ReactElement) => option.props.value !== '')}
          </ul>
        )}

        {native && (
          <select
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocus}
            defaultValue={defaultValue}
            disabled={disabled}
            onBlur={() => this.setState({ focused: false })}
            onChange={event => this.handleChange(event, event.target.value)}
            onFocus={() => this.setState({ focused: true })}
            {...css(styles.nativeSelect.base)}
            value={value}
          >
            {options}
          </select>
        )}
      </span>
    )
  }
}

export interface ISelectOptionProps {
  children?: React.ReactNode
  value: any
  onClick?: (event: React.SyntheticEvent) => void
  selected?: boolean
  disabled?: boolean
}

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
export const SelectOption: React.FC<ISelectOptionProps> = ({ children, value, onClick, selected, disabled }) => {
  const styles = getStyles(useTheme())
  return (
    <li
      {...css([
        styles.option.base,
        !disabled && styles.option.normal,
        !disabled && selected && styles.option.selected,
        disabled && styles.option.disabled,
      ])}
    >
      <div
        onClick={onClick}
        role="button"
        tabIndex={0}
        {...css(styles.optionLabel.base)}
        onKeyPress={event => onClick && (event.key === 'Enter' || event.key === ' ') && onClick(event)}
      >
        <CheckmarkIcon visible={selected && value !== ''} /> {children}
      </div>
    </li>
  )
}

SelectOption.propTypes = {
  children: PropTypes.node,
  value: PropTypes.any,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
}

SelectOption.displayName = 'SelectOption'

interface IChevronIconProps {
  active?: boolean
  color?: UiVariant | 'brand' | 'white'
}

const ChevronIcon: React.FC<IChevronIconProps> = ({ active, color }) => {
  const theme = useTheme()
  const styles = getStyles(theme)
  return (
    <Flex alignItems="center" row style={[styles.chevronIcon.base, active && styles.chevronIcon.active]}>
      <ChevronIconBase
        {...css(styles.chevronIcon.svg)}
        color={
          (color && theme.colors.accents[color]) || (active ? theme.colors.accents.primary : theme.colors.ui.medium)
        }
      />
    </Flex>
  )
}

ChevronIcon.propTypes = {
  active: PropTypes.bool,
  color: PropTypes.oneOf(['cta', 'primary', 'secondary', 'positive', 'negative', 'alert', 'brand', 'white']),
}

interface ICheckmarkIconProps {
  visible?: boolean
}

const CheckmarkIcon: React.FC<ICheckmarkIconProps> = ({ visible }) => {
  const styles = getStyles(useTheme())
  return <i {...css(styles.checkmarkIcon.base)}>{visible && <Check />}</i>
}

CheckmarkIcon.propTypes = {
  visible: PropTypes.bool,
}

export const Select = (withTheme(
  injectIntl(clickDisplay(WrappedSelect as any) as any) as any
) as unknown) as React.ComponentClass<INonNativeSelectProps<any> & INativeSelectProps>
